// The exercise catalog: the 20 starter exercises first (stable ids), then the groups.
// Levels, extra aliases and camera counter patterns are applied here from their own files.
// Sources and licenses: docs/research.md, sections 5 and 6. All steps are our own wording.
import type { Counting, Exercise } from '../types';
import { ACCESSORIES } from './accessories';
import { ALIASES } from './aliases';
import { BODYWEIGHT } from './bodyweight';
import { CARDIO } from './cardio';
import { COORDINATION } from './coordination';
import { CORE } from './core';
import { DEMO_FAMILY_OF } from './demo-families';
import { ENDURANCE, MACHINE_SPORTS } from './endurance';
import { COUNTER_BENCHMARKS, COUNTER_REFS, WORKOUTVISION_PATTERNS } from './counter-patterns';
import { FREE_WEIGHTS } from './free-weights';
import { KETTLEBELL } from './kettlebell';
import { LEVELS } from './levels';
import { MACHINES } from './machines';
import { MOBILITY } from './mobility';
import { STARTER_EXERCISES } from './starter';

export { MUSCLES } from './define';
export { DEMO_FAMILIES, DEMO_FAMILY_OF, demoFamilyOf, demoPropsOf, demoPropsProblem, guessDemoFamily, isDemoFamily, NO_DEMO_FAMILY, type DemoFamilyInfo } from './demo-families';

/** Camera status from the pattern table: reliable stays reliable, a known pattern is experimental, else not set up. */
function withCounter(e: Exercise): Counting {
  if (e.counting.mode !== 'camera') return e.counting;
  const ref = COUNTER_REFS[e.id];
  const pattern = ref ? WORKOUTVISION_PATTERNS[ref] : undefined;
  if (!ref || !pattern) return { mode: 'camera', status: 'not_set_up' };
  const benchmark = COUNTER_BENCHMARKS[e.id];
  return { mode: 'camera', status: e.counting.status === 'reliable' ? 'reliable' : 'experimental', counterPattern: pattern, counterRef: ref, ...(benchmark ? { benchmark } : {}) };
}

function withAliases(e: Exercise): string[] | undefined {
  const all = [...(e.aliases ?? []), ...(ALIASES[e.id] ?? [])];
  const seen = new Set<string>();
  const unique = all.filter((a) => {
    const key = a.toLowerCase();
    if (seen.has(key) || key === e.name.toLowerCase()) return false;
    seen.add(key);
    return true;
  });
  return unique.length ? unique : undefined;
}

const GROUPS: Exercise[] = [
  ...STARTER_EXERCISES,
  ...BODYWEIGHT,
  ...CORE,
  ...CARDIO,
  ...MOBILITY,
  ...KETTLEBELL,
  ...FREE_WEIGHTS,
  ...MACHINES,
  ...ACCESSORIES,
  ...ENDURANCE,
  ...COORDINATION,
];

/** The cardio machines and jump rope get their sport here, so their own files stay as they are. */
function withSport(e: Exercise): Pick<Exercise, 'sport' | 'activity'> {
  const sport = e.sport ?? MACHINE_SPORTS[e.id];
  if (sport) return { sport, activity: 'cardio' };
  if (e.id === 'ski_erg') return { activity: 'cardio' };
  return {};
}

export const CATALOG: Exercise[] = GROUPS.map((e) => {
  const aliases = withAliases(e);
  const demo = DEMO_FAMILY_OF[e.id];
  return {
    ...e, ...(aliases ? { aliases } : {}), ...withSport(e), counting: withCounter(e), level: LEVELS[e.id],
    ...(demo?.family ? { demoFamily: demo.family, ...(demo.props ? { demoProps: demo.props } : {}) } : {}),
  };
});
